import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { TemplateField, TemplateFieldRuleSetRules } from "@/integrations/supabase/templateEditor";
import type { Datapoint } from "@/types/dataset";
import { Trash2 } from "lucide-react";
import { useState } from "react";

export interface TemplateFieldWithDatapoints extends TemplateField {
  field: TemplateField["field"] & {
    dataset: {
      datapoints: Datapoint[];
    };
  };
}

interface RuleProps {
  rule: TemplateFieldRuleSetRules[number];
  availableTemplateFields: TemplateFieldWithDatapoints[];
  onDelete: () => void;
  onUpdate: (updateArgs: { fieldId: string; datapointId: string; operator: "AND" | "NOT" }) => void;
}

export const Rule = ({ rule, availableTemplateFields, onDelete, onUpdate }: RuleProps) => {
  const [fieldId, setFieldId] = useState(rule.field_id ?? "");
  const [datapointId, setDatapointId] = useState(rule.datapoint_id ?? "");
  const [operator, setOperator] = useState<"AND" | "NOT">(rule.operator === "NOT" ? "NOT" : "AND");

  const handleFieldChange = (fieldId: string) => {
    if (fieldId !== rule.field_id) {
      setFieldId(fieldId);
      onUpdate({
        fieldId,
        datapointId,
        operator,
      });
    }
  };

  const handleValueChange = (datapointId: string) => {
    if (datapointId !== rule.datapoint_id) {
      setDatapointId(datapointId);
      onUpdate({
        fieldId,
        datapointId,
        operator,
      });
    }
  };

  const handleOperatorChange = (operator: "AND" | "NOT") => {
    if (operator !== rule.operator) {
      setOperator(operator);
      onUpdate({
        fieldId,
        datapointId,
        operator,
      });
    }
  };

  return (
    <div className="flex items-center gap-2 p-2 border rounded-md bg-muted/20">
      <Select value={operator} onValueChange={(value) => handleOperatorChange(value as "AND" | "NOT")}>
        <SelectTrigger className="h-8 w-24">
          <SelectValue placeholder="Operator" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="AND">AND</SelectItem>
          <SelectItem value="NOT">NOT</SelectItem>
        </SelectContent>
      </Select>

      <Select value={fieldId} onValueChange={handleFieldChange} disabled={availableTemplateFields.length === 0}>
        <SelectTrigger className="h-8 flex-grow">
          <SelectValue placeholder="Select field" />
        </SelectTrigger>
        <SelectContent>
          {availableTemplateFields.map((templateField) => (
            <SelectItem key={templateField.field.id} value={templateField.field.id}>
              {templateField.field.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={datapointId} onValueChange={handleValueChange} disabled={availableTemplateFields.length === 0}>
        <SelectTrigger className="h-8 flex-grow">
          <SelectValue placeholder="Select value" />
        </SelectTrigger>
        <SelectContent>
          {availableTemplateFields
            .find((tField) => tField.field_id === fieldId)
            ?.field.dataset.datapoints.map((datapoint) => (
              <SelectItem key={datapoint.id} value={datapoint.id}>
                {datapoint.name}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>

      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8 flex-shrink-0 text-destructive hover:text-destructive"
        onClick={onDelete}
      >
        <Trash2 className="h-4 w-4" />
        <span className="sr-only">Delete rule</span>
      </Button>
    </div>
  );
};
